
import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
      setSubmitStatus('error');
      setStatusMessage('Please fill in all required fields.');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setStatusMessage('Message must be 500 characters or less.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('name', formData.name.trim());
      formDataToSubmit.append('email', formData.email.trim());
      formDataToSubmit.append('subject', formData.subject.trim());
      formDataToSubmit.append('message', formData.message.trim());

      const response = await fetch('https://readdy.ai/api/form/d30u3nn348pq0eno6f1g', {
        method: 'POST',
        body: formDataToSubmit,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setStatusMessage('Thank you for your message! We\'ll get back to you soon.');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setSubmitStatus('error');
      setStatusMessage('Sorry, there was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const remainingChars = 500 - formData.message.length;

  // SEO Schema.org data for contact page
  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact DBD Podcast",
    "description": "Get in touch with DBD Podcast. Send us your questions, guest suggestions, or partnership opportunities.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
    "mainEntity": {
      "@type": "Organization",
      "name": "DBD Podcast",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "General Inquiry",
        "availableLanguage": "English"
      }
    }
  };

  return (
    <div className="min-h-screen pt-16">
      <SEOHead
        title="Contact DBD Podcast | Get in Touch"
        description="Contact DBD Podcast for general inquiries, guest suggestions, partnership opportunities, or media requests. We typically respond within 24-48 hours during business days."
        keywords="contact DBD podcast, podcast guest suggestions, partnership opportunities, media inquiries, podcast contact form"
        ogTitle="Contact DBD Podcast | Get in Touch"
        ogDescription="Contact DBD Podcast for general inquiries, guest suggestions, partnership opportunities, or media requests."
        ogType="website"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`}
      />
      
      <SchemaOrg data={contactPageSchema} />

      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/e0d8b8eb4fb5d8fda5e2eed1ee34fe0a.jpg')`
        }}
      >
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Have questions about DBD Podcast? Want to suggest a guest or topic? 
              We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-8">Let's Connect</h2>
                
                <div className="space-y-6 mb-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <i className="ri-mail-line text-amber-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">Email Us</h3>
                      <p className="text-gray-600">
                        For general inquiries, guest suggestions, or partnership opportunities.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <i className="ri-time-line text-amber-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">Response Time</h3>
                      <p className="text-gray-600">
                        We typically respond within 24-48 hours during business days.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <i className="ri-mic-line text-amber-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">Guest Submissions</h3>
                      <p className="text-gray-600">
                        Interested in being a guest? Tell us about your expertise and story.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="font-semibold text-gray-800 mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-amber-100 transition-colors cursor-pointer">
                      <i className="ri-twitter-fill text-gray-600 hover:text-amber-600"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-amber-100 transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill text-gray-600 hover:text-amber-600"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-amber-100 transition-colors cursor-pointer">
                      <i className="ri-youtube-fill text-gray-600 hover:text-amber-600"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-amber-100 transition-colors cursor-pointer">
                      <i className="ri-spotify-fill text-gray-600 hover:text-amber-600"></i>
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Send us a Message</h3>
                
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer"
                    >
                      <option value="">Select a subject</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Guest Suggestion">Guest Suggestion</option>
                      <option value="Partnership Opportunity">Partnership Opportunity</option>
                      <option value="Technical Support">Technical Support</option>
                      <option value="Media/Press">Media/Press</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 resize-vertical"
                      placeholder="Tell us how we can help you..."
                    />
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-xs text-gray-500">
                        Please limit your message to 500 characters.
                      </p>
                      <p className={`text-xs ${remainingChars < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                        {remainingChars} characters remaining
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="privacy-agree"
                      name="privacyAgree"
                      required
                      className="mt-1 h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
                    />
                    <label htmlFor="privacy-agree" className="text-sm text-gray-600 cursor-pointer">
                      I agree to the{' '}
                      <Link to="/privacy" className="text-amber-600 hover:text-amber-700 underline cursor-pointer">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="ri-check-circle-line text-green-500 text-xl mr-3"></i>
                        <p className="text-green-700 text-sm">{statusMessage}</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                        <p className="text-red-700 text-sm">{statusMessage}</p>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || remainingChars < 0}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Sending Message...
                      </span>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">
                Quick answers to common questions about DBD Podcast
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-semibold text-gray-800 mb-3">How can I suggest a guest?</h3>
                <p className="text-gray-600 text-sm">
                  Use the contact form above with "Guest Suggestion" as the subject. 
                  Include details about the person's expertise and why they'd be a great fit.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-semibold text-gray-800 mb-3">Can I be a guest on the show?</h3>
                <p className="text-gray-600 text-sm">
                  Absolutely! Tell us about your expertise, experience, and what unique 
                  insights you can share with our audience.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-semibold text-gray-800 mb-3">Do you offer sponsorship opportunities?</h3>
                <p className="text-gray-600 text-sm">
                  Yes, we work with select partners. Contact us with "Partnership Opportunity" 
                  to discuss sponsorship and advertising options.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-semibold text-gray-800 mb-3">How often do you release episodes?</h3>
                <p className="text-gray-600 text-sm">
                  We release new episodes weekly. Subscribers get early access one week 
                  before public release.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
